import React, { useMemo, useState, useEffect, useCallback } from 'react';

import Translator from 'ggApp/modules/translator/components/PureTranslator';
import { useMachine } from '@xstate/react';
import { ContractState } from 'ggApp/types/graphql';
import { SwapCheckout } from 'ggApp/modules/SwapCheckout';
import { swapMachine } from 'ggApp/modules/SwapCheckout/swapMachine';
import { useValidateMixCard } from '../useValidate';

import { Button, ButtonText, Warning } from './styles';

interface MixCtaProps {
    onCtaClick: (type: string) => void;
    inFlexCartFlow: boolean;
    products: any;
    activeProduct: any;
    contracts?: any;
}

const warningModes = {
    unableToAddToMixCart: 'MIXABLE_CARD_WARNING',
    willResetFlexCart: 'MIX_MIXABLE_RENTAL_PLAN_WARNING',
    mixCantSwapYet: 'MIX_HAS_ACTIVE_CONTRACT_WARNING',
    mixContractUnderReview: 'MIX_REVIEWING_CONTRACT_WARNING',
    swapIsOngoing: 'SWAP_ALREADY_ONGOING_WARNING',
};

enum unavailabilityReason {
    TOO_EARLY = 'TOO_EARLY',
    CONTRACT_NOT_ACTIVE = 'CONTRACT_NOT_ACTIVE',
    SWAP_ALREADY_ONGOING = 'SWAP_ALREADY_ONGOING',
}

export const MixCta = ({
    products,
    activeProduct,
    onCtaClick,
    inFlexCartFlow,
    contracts,
}: MixCtaProps): JSX.Element => {
    const [current, send] = useMachine(swapMachine);

    const [warningState, setWarningState] = useState<string | null>(null);
    const [timeUntilAvailable, setTimeUntilAvailable] = useState(null);
    const [swapModal, setSwapModal] = useState<{ openModal: () => void } | null>(null);
    const createModalRef = useCallback((newModal): void => setSwapModal(newModal), []);

    const { valid, loading } = useValidateMixCard({
        products: [
            ...products.map((p: any) => ({
                categoryId: p?.categoryId,
                subCategoryId: p?.subCategoryId,
                variantSku: p.variantSku,
                productSku: p.productSku,
            })),
            {
                categoryId: activeProduct?.categoryId,
                subCategoryId: activeProduct?.subCategoryId,
                variantSku: activeProduct?.variantSku,
                productSku: activeProduct?.productSku,
            },
        ],
    });

    const activeContract = contracts?.[0]?.state === ContractState.ACTIVE;
    const pendingContract =
        contracts?.[0]?.state === ContractState.FULFILLING ||
        contracts?.[0]?.state === ContractState.DRAFT;

    const hasMixContract = useMemo<boolean>(
        () => Boolean(contracts && contracts.length > 0 && (activeContract || pendingContract)),
        [contracts, activeContract, pendingContract],
    );

    useEffect(() => {
        if (
            activeContract &&
            contracts?.[0]?.swappingTerms?.unavailabilityReason === unavailabilityReason.TOO_EARLY
        ) {
            setWarningState(warningModes.mixCantSwapYet);
            setTimeUntilAvailable(contracts?.[0]?.swappingTerms?.timeUntilAvailable?.value ?? '');
        } else if (pendingContract) {
            setWarningState(warningModes.mixContractUnderReview);
        } else if (
            contracts?.[0]?.swappingTerms?.unavailabilityReason ===
            unavailabilityReason.SWAP_ALREADY_ONGOING
        ) {
            setWarningState(warningModes.swapIsOngoing);
        } else if (inFlexCartFlow) {
            setWarningState(warningModes.willResetFlexCart);
        } else if (!loading) {
            setWarningState(!valid ? warningModes.unableToAddToMixCart : null);
        }
    }, [contracts, activeContract, pendingContract, inFlexCartFlow, valid, loading]);

    const onBtnClick = useCallback(() => {
        if (!hasMixContract) {
            onCtaClick('MIX');
            return;
        }

        if (swapModal) {
            swapModal.openModal();
        }
    }, [swapModal, onCtaClick, hasMixContract]);

    const buttonDisabled = loading || Boolean(warningState);

    return (
        <>
            {warningState ? (
                <Warning
                    text={
                        <Translator tk={warningState} withValues={{ days: timeUntilAvailable }} />
                    }
                />
            ) : null}

            <Button
                isDisabled={buttonDisabled}
                fullWidth
                color="red"
                data-testid="mix-card-cta-id"
                onClick={onBtnClick}
            >
                <ButtonText>
                    <Translator
                        tk={activeContract ? 'MIXABLE_CARD_SWAP_CTA' : 'MIXABLE_CARD_CTA'}
                    />
                </ButtonText>
            </Button>

            <SwapCheckout
                modalRef={createModalRef}
                current={current}
                send={send}
                modal={swapModal}
                swapedItems={{ newItem: activeProduct, oldItems: contracts?.[0]?.goods }}
            />
        </>
    );
};
